import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../admin/AdminLayout';
import { AdminAuthContext } from '../../context/AdminAuthContext';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';

const SUPERADMIN_PERMISSIONS = {
  canView: ['superadmin_shell', 'settings'],
  canEdit: ['superadmin_shell', 'settings'],
};

const mapAdminSearchResults = (admins = []) =>
  admins
    .map((admin) => {
      const primaryShop = admin.shops?.[0] || null;
      if (!primaryShop) return null;
      return {
        id: `${admin.id}-${primaryShop.id}`,
        type: 'shop',
        title: `${admin.name || 'Admin'} • ${primaryShop.name || 'Shop'}`,
        subtitle: `${admin.email || 'No email'} • /${primaryShop.slug || 'n/a'}`,
        status: primaryShop.subscription_status || 'active',
        url: `/superadmin/shops/${primaryShop.id}`,
      };
    })
    .filter(Boolean);

const SuperadminLayout = ({ children }) => {
  const navigate = useNavigate();
  const superadmin = useSuperadminAuth();
  const { t } = useTranslation('admin');

  const NAV_ITEMS = useMemo(() => [
    { path: '/superadmin/dashboard', label: t('admin.superadmin_nav_dashboard'), icon: 'dashboard', permission: 'superadmin_shell' },
    { path: '/superadmin/shops', label: t('admin.superadmin_nav_explorer'), icon: 'users', permission: 'superadmin_shell' },
    { path: '/superadmin/provision', label: t('admin.superadmin_nav_provision'), icon: 'settings', permission: 'superadmin_shell' },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  const adminShellValue = useMemo(() => ({
    user: superadmin.user,
    shopId: null,
    permissions: SUPERADMIN_PERMISSIONS,
    loading: superadmin.loading,
    error: superadmin.error,
    fetchUserData: superadmin.fetchUserData,
    logout: superadmin.logout,
    hasPermission: (permission) => ['superadmin_shell', 'settings'].includes(permission),
    canEdit: () => true,
    hasAdminAccess: () => superadmin.hasSuperadminAccess(),
  }), [superadmin]);

  const shellConfig = useMemo(() => ({
    user: superadmin.user,
    logout: superadmin.logout,
    hasPermission: (permission) => ['superadmin_shell', 'settings'].includes(permission),
    navItems: NAV_ITEMS,
    dropdownItems: [],
    disableInventoryBadge: true,
    disableBrandingFetch: true,
    hideBottomNav: true,
    hideCredits: true,
    searchPlaceholder: t('admin.superadmin_search_placeholder'),
    mobileSearchPlaceholder: t('admin.superadmin_mobile_search_placeholder'),
    handleSearchSubmit: (query, nextNavigate = navigate) => {
      nextNavigate(`/superadmin/shops?q=${encodeURIComponent(query)}`);
    },
    globalSearch: async (query) => {
      const response = await superadmin.getAdmins({ q: query, page: 1, pageSize: 8 });
      return mapAdminSearchResults(response.data || []);
    },
    profilePath: '/superadmin/profile',
    settingsPath: '/superadmin/system',
    pricingPath: '/superadmin/provision',
    contactPath: '/superadmin/audit-logs',
    creditsPath: '/superadmin/dashboard',
    notificationsPath: '/superadmin/notifications',
    logoutRedirectPath: '/?auth=1',
  }), [navigate, superadmin, NAV_ITEMS, t]);

  return (
    <AdminAuthContext.Provider value={adminShellValue}>
      <AdminLayout shellConfig={shellConfig}>{children}</AdminLayout>
    </AdminAuthContext.Provider>
  );
};

export default SuperadminLayout;
